import { motion } from 'framer-motion';
import { BoltIcon, HeadsetIcon, ShieldIcon, UsersIcon } from './LandingIcons';

interface FeatureCardProps {
  feature: {
    icon: React.ReactNode;
    title: string;
    description: string;
  };
  index: number;
}

/**
 * Features section with animated cards.
 */
export function LandingFeatures() {
  const features = [
    {
      icon: <HeadsetIcon />,
      title: 'VR-First Design',
      description: 'Purpose-built for Meta Quest and other VR headsets. Your web participants join from any browser.',
    },
    {
      icon: <UsersIcon />,
      title: 'Persistent Rooms',
      description: 'Create dedicated spaces for your team. Members can drop in anytime, no scheduling required.',
    },
    {
      icon: <BoltIcon />,
      title: 'Fast Room Access',
      description: 'Jump directly into your team rooms from web or VR with a streamlined, repeatable flow.',
    },
    {
      icon: <ShieldIcon />,
      title: 'Private & Secure',
      description: 'End-to-end encrypted video and audio. Your conversations stay between you and your team.',
    },
  ];

  return (
    <section className="py-24 px-6">
      <div className="max-w-6xl mx-auto">
        <motion.div
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="text-center mb-16"
        >
          <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 mb-4">
            Designed for how teams actually work
          </h2>
          <p className="text-gray-500 text-lg max-w-2xl mx-auto">
            Whether you're in VR or at your desk, VR Meet brings everyone together.
          </p>
        </motion.div>

        <div className="grid sm:grid-cols-2 gap-6">
          {features.map((feature, index) => (
            <FeatureCard key={feature.title} feature={feature} index={index} />
          ))}
        </div>
      </div>
    </section>
  );
}

/**
 * Individual feature card with hover effects.
 */
function FeatureCard({ feature, index }: FeatureCardProps) {
  return (
    <motion.div
      initial={{ y: 30, opacity: 0 }}
      whileInView={{ y: 0, opacity: 1 }}
      viewport={{ once: true }}
      transition={{ duration: 0.5, delay: index * 0.1 }}
      whileHover={{ y: -4 }}
      className="group relative p-8 rounded-2xl bg-white border border-gray-100 shadow-lg shadow-gray-100/50 hover:shadow-xl hover:shadow-gray-200/50 hover:border-gray-200 transition-all cursor-default"
    >
      <div className="absolute inset-0 rounded-2xl bg-gradient-to-br from-brand-blue/5 via-transparent to-brand-green/5 opacity-0 group-hover:opacity-100 transition-opacity" />

      <div className="relative">
        <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-brand-blue/10 to-brand-teal/10 flex items-center justify-center mb-5 group-hover:scale-110 transition-transform">
          <div className="text-brand-teal">{feature.icon}</div>
        </div>

        <h3 className="text-xl font-semibold text-gray-900 mb-3">{feature.title}</h3>
        <p className="text-gray-500 leading-relaxed">{feature.description}</p>
      </div>
    </motion.div>
  );
}
